import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import Banner from "@dtci/spellbook/Banner";
import Button from "@dtci/spellbook/Button";
import ContentBlock from "@dtci/spellbook/ContentBlock";
import DataGrid from "@dtci/spellbook/DataGrid";
import Typography from "@dtci/spellbook/Typography";
import Moment from "moment-timezone";
import { useConfirmationDialog } from "tap/components/dialogs/dialogs/confirmation.jsx";
import { useAgGridApi } from "tap/components/AGGrid/hooks.js";
import { useWindowsGridUtils } from "./utils/gridUtils";
import { useWindowsData } from "../../hooks/useWindowsData";
import { useWindowsActions } from "tap/store/windows/useWindows.js";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { usePermissions } from "tap/utils/permissions.js";
import { tapApi } from "tap/api.js";
import {
  headers,
  rename,
  transformWindowsForExport,
} from "../../utils/windowsExport";
import { exportToCsvWithHeaders } from "tap/utils/exportToCsv.js";
import { usePaginatedGridSelection } from "tap/utils/hooks/usePaginatedGridSelection.js";
import { getAllItemsRange } from "tap/utils/pagination.js";
import gridStyles from "../../../../components/AGGrid/details-table.module.scss";
import "./styles.scss";

export const WindowsGrid = () => {
  const agGridApi = useAgGridApi();
  const { openConfirmationDialog } = useConfirmationDialog();
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast, showSuccessToast } = useToasts();
  const { isTapAdmin } = usePermissions();

  const { windowSequences, refreshWindows, totalItems } = useSelector(
    (s) => s.windows,
  );
  const { fetchWindows, updateWindows, windowsRange, filterForm } =
    useWindowsData();
  const { updateWindows: updateWindowsAction } = useWindowsActions();

  const fetchAllRows = useCallback(async () => {
    if (!totalItems) {
      return [];
    }
    const data = await fetchWindows(getAllItemsRange(totalItems), filterForm);
    return data?.content ?? [];
  }, [fetchWindows, filterForm, totalItems]);

  const {
    bannerText,
    onSelectionChanged,
    onRowSelected,
    clearSelection,
    resolveSelectedRows,
  } = usePaginatedGridSelection({
    totalItems,
    fetchAllRows,
    entityName: "item",
    rowData: windowSequences ?? [],
    gridApiRef: agGridApi.AgGridApiRef,
    resetKey: `${totalItems}-${windowsRange}-${JSON.stringify(filterForm)}`,
  });

  const onOpenViewDrawer = useCallback(
    async (windowSequence) => {
      if (!windowSequence || !windowSequence.id) return;
      setLoadingTrue();
      try {
        const [data, historyData] = await Promise.all([
          tapApi.windowSequenceById(windowSequence.id),
          tapApi.windowSequenceHistory(windowSequence.id),
        ]);

        if (data?.code || data?.httpCode || data?.error) {
          showErrorToast(
            data?.message || "Failed to load window sequence details.",
          );
          return;
        }

        const mappedHistory = Array.isArray(historyData)
          ? historyData.map((h) => ({
              label: `${Moment(Moment.utc(h.historyCreatedAt).toDate())
                .tz(Moment.tz.guess())
                .format(
                  "DD-MMM-YYYY, h:mm:ss A z",
                )} | ${h.createdBy?.username ?? ""}`,
              value: h.id,
              data: h,
            }))
          : [];

        updateWindowsAction({
          isViewDrawerOpen: true,
          selectedWindowSequence: data,
          windowSequenceHistory: mappedHistory,
        });
      } catch (error) {
        showErrorToast("Failed to load window sequence details.");
        console.error("Error loading window sequence details:", error);
      } finally {
        setLoadingFalse();
      }
    },
    [setLoadingTrue, setLoadingFalse, showErrorToast, updateWindowsAction],
  );

  const onOpenEditDrawer = useCallback(
    async (data) => {
      if (!data || !data.id) return;

      setLoadingTrue();
      try {
        const [transactionalWindows, windowSequence] = await Promise.all([
          tapApi.transactionalWindows(),
          tapApi.windowSequenceById(data?.id),
        ]);

        if (
          windowSequence?.code ||
          windowSequence?.httpCode ||
          windowSequence?.error
        ) {
          showErrorToast(
            windowSequence?.message ||
              "Failed to load window sequence for editing.",
          );
          return;
        }

        updateWindowsAction({
          transactionalWindowsOptions: transactionalWindows || [],
          isEditDrawerOpen: true,
          selectedWindowSequence: windowSequence,
        });
      } catch (error) {
        showErrorToast("Failed to load window sequence for editing.");
        console.error("Error loading window sequence:", error);
      } finally {
        setLoadingFalse();
      }
    },
    [setLoadingTrue, setLoadingFalse, updateWindowsAction, showErrorToast],
  );

  const { columns } = useWindowsGridUtils({
    columnHandlers: [onOpenViewDrawer, onOpenEditDrawer],
    isTapAdmin,
  });

  const handleClose = () => {
    clearSelection();
  };

  const refreshWindowsList = async () => {
    const data = await fetchWindows(windowsRange, filterForm);
    if (data) {
      updateWindows({
        windowSequences: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber,
      });
    }
  };

  useEffect(() => {
    if (refreshWindows) {
      refreshWindowsList();
      updateWindowsAction({ refreshWindows: false });
    }
  }, [refreshWindows]);

  /** Factory for bulk action handlers - reduces duplication for copy/inactivate operations */
  const createBulkActionHandler =
    ({
      actionName,
      singleApiCall,
      bulkApiCall,
      successMessageSingle,
      successMessageMultiple,
      errorMessage,
    }) =>
    async () => {
      const selectedRows = await resolveSelectedRows();
      if (selectedRows.length === 0) {
        return;
      }
      const isSingleRow = selectedRows.length === 1;
      const title = isSingleRow
        ? `${actionName} Window`
        : `${actionName} Windows`;
      const message = isSingleRow
        ? `Are you sure you want to ${actionName.toLowerCase()} this window?`
        : `Are you sure you want to ${actionName.toLowerCase()} the selected windows?`;

      openConfirmationDialog(title, "Confirm", [message], async () => {
        setLoadingTrue();
        try {
          let result;
          if (isSingleRow) {
            result = await singleApiCall(selectedRows[0].id);
          } else {
            const ids = selectedRows.map((row) => row.id);
            result = await bulkApiCall(ids);
          }

          if (result?.code || result?.httpCode || result?.error) {
            showErrorToast(
              result?.message || result?.error?.message || errorMessage,
            );
          } else {
            await refreshWindowsList();
            showSuccessToast(
              isSingleRow ? successMessageSingle : successMessageMultiple,
            );
          }
        } catch (error) {
          showErrorToast(errorMessage);
          console.error(`Error ${actionName.toLowerCase()} window(s):`, error);
        } finally {
          handleClose();
          setLoadingFalse();
        }
      });
    };

  const handleCopy = createBulkActionHandler({
    actionName: "Copy",
    singleApiCall: tapApi.copyWindowSequence,
    bulkApiCall: tapApi.copyWindowSequenceBulk,
    successMessageSingle: "Window copied successfully.",
    successMessageMultiple: "Windows copied successfully.",
    errorMessage: "Error copying window(s).",
  });

  const handleInactivate = createBulkActionHandler({
    actionName: "Inactivate",
    singleApiCall: tapApi.inactivateWindowSequence,
    bulkApiCall: tapApi.inactivateWindowSequenceBulk,
    successMessageSingle: "Window inactivated successfully.",
    successMessageMultiple: "Windows inactivated successfully.",
    errorMessage: "Error inactivating window(s).",
  });

  const handleExport = async () => {
    const selectedRows = await resolveSelectedRows();
    if (selectedRows.length === 0) {
      return;
    }
    setLoadingTrue();
    try {
      const ids = selectedRows.map((row) => row.id);
      const data = await tapApi.exportWindowSequence(ids);

      if (data?.code || data?.httpCode || data?.error) {
        showErrorToast(
          data?.message || "Failed to export window sequence data.",
        );
        return;
      }

      const flatRows = transformWindowsForExport(data?.content || data);
      exportToCsvWithHeaders(flatRows, headers, rename, "windows");
    } catch (error) {
      showErrorToast("Error exporting window sequences.");
      console.error("Error exporting window sequence(s):", error);
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <>
      {bannerText && (
        <Banner
          faPackage="far"
          iconTypePrimary={"fa-check-circle"}
          role="status"
          type={"success"}
          onClose={handleClose}
          className="windows-selection-banner"
        >
          <ContentBlock className="windows-banner-content">
            <Typography className="main-banner-text" variant="T20">
              {bannerText}
            </Typography>
            <div className="windows-banner-actions">
              {isTapAdmin && (
                <Button
                  className="windows-banner-button"
                  iconType="fa-solid fa-copy"
                  type="button"
                  styleType="link"
                  onClick={handleCopy}
                >
                  COPY
                </Button>
              )}
              {isTapAdmin && (
                <Button
                  className="windows-banner-button-danger"
                  iconType="fa-solid fa-trash"
                  type="button"
                  styleType="link"
                  onClick={handleInactivate}
                >
                  INACTIVATE
                </Button>
              )}
              <Button
                className="windows-banner-button"
                iconType="fa-solid fa-arrow-down-to-line"
                type="button"
                styleType="link"
                onClick={handleExport}
              >
                EXPORT
              </Button>
            </div>
          </ContentBlock>
        </Banner>
      )}
      <DataGrid
        className={gridStyles.dataGrid}
        autoSizing
        agGrid={{
          ref: agGridApi.AgGridApiRef,
          rowData: windowSequences ?? [],
          suppressColumnStateReset: true,
          headerHeight: 45,
          onSelectionChanged,
          onRowSelected,
          getRowId: (params) => String(params.data.id),
          suppressScrollOnNewData: true,
        }}
        bordered="none"
        columnDefs={columns}
        defaultColumnDef={{
          wrapText: true,
          autoHeight: true,
          resizable: true,
          suppressHeaderMenuButton: true,
          cellStyle: {
            paddingTop: "10px",
            paddingBottom: "10px",
            wordBreak: "break-word",
          },
        }}
        sortable={false}
        striped
        selectable
        noResultsMessage={"No Window Sequences Found"}
      />
    </>
  );
};
